/**
 * CSVImportDialog - Modal de importacao de entregas via CSV
 */

import { useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  RadioGroup,
  Radio,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Description as FileIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';
import { parseDeliveriesCSV, generateExampleCSV } from '../utils/csvParser';

const MAX_PREVIEW_ROWS = 10;
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const CSVImportDialog = ({
  open,
  onClose,
  onImport,
  existingDeliveriesCount = 0,
}) => {
  const tokens = usePremiumTokens();
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [parseResult, setParseResult] = useState(null);
  const [importMode, setImportMode] = useState('merge');
  const [isProcessing, setIsProcessing] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Reset ao fechar
  const handleClose = useCallback(() => {
    setFile(null);
    setParseResult(null);
    setImportMode('merge');
    setIsProcessing(false);
    setDragOver(false);
    onClose();
  }, [onClose]);

  // Processar arquivo
  const processFile = useCallback(async (selectedFile) => {
    if (!selectedFile) return;

    // Validar tipo
    const validTypes = ['text/csv', 'text/plain', 'application/vnd.ms-excel'];
    const validExtensions = ['.csv', '.txt'];
    const hasValidExtension = validExtensions.some((ext) =>
      selectedFile.name.toLowerCase().endsWith(ext));

    if (!validTypes.includes(selectedFile.type) && !hasValidExtension) {
      setParseResult({
        deliveries: [],
        errors: [{ line: 0, message: 'Tipo de arquivo invalido. Use CSV ou TXT.' }],
        warnings: [],
      });
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      setParseResult({
        deliveries: [],
        errors: [{ line: 0, message: `Arquivo muito grande (${(selectedFile.size / 1024 / 1024).toFixed(1)}MB). Maximo: ${MAX_FILE_SIZE_MB}MB.` }],
        warnings: [],
      });
      return;
    }

    setIsProcessing(true);
    setFile(selectedFile);

    try {
      const content = await selectedFile.text();
      const result = parseDeliveriesCSV(content);
      setParseResult(result);
    } catch (error) {
      setParseResult({
        deliveries: [],
        errors: [{ line: 0, message: `Erro ao ler arquivo: ${error.message}` }],
        warnings: [],
      });
    } finally {
      setIsProcessing(false);
    }
  }, []);

  // Handler de upload via input
  const handleFileSelect = useCallback((event) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      processFile(selectedFile);
    }
  }, [processFile]);

  // Handlers de drag & drop
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      processFile(droppedFile);
    }
  }, [processFile]);

  // Download arquivo de exemplo
  const handleDownloadExample = useCallback(() => {
    const content = generateExampleCSV();
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'entregas_exemplo.csv';
    link.click();
    URL.revokeObjectURL(url);
  }, []);

  // Confirmar importacao
  const handleConfirmImport = useCallback(() => {
    if (!parseResult?.deliveries.length) return;

    onImport(parseResult.deliveries, importMode);
    handleClose();
  }, [parseResult, importMode, onImport, handleClose]);

  const hasErrors = parseResult?.errors?.length > 0;
  const hasWarnings = parseResult?.warnings?.length > 0;
  const hasDeliveries = parseResult?.deliveries?.length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: tokens?.surface?.raised,
          borderRadius: tokens?.radius?.xl,
          border: `1px solid ${tokens?.border?.subtle}`,
          maxHeight: '85vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${tokens?.border?.subtle}`,
          py: 1.5,
          px: 2,
        }}
      >
        <Typography sx={{ fontWeight: 600, color: tokens?.text?.primary }}>
          Importar Entregas via CSV
        </Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon sx={{ color: tokens?.text?.secondary }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Area de Upload */}
        <Box sx={{ p: 2 }}>
          <Box
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: `2px dashed ${dragOver ? tokens?.accent?.primary : tokens?.border?.default}`,
              borderRadius: tokens?.radius?.lg,
              backgroundColor: dragOver ? `${tokens?.accent?.primary}10` : tokens?.surface?.elevated,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: tokens?.accent?.primary,
                backgroundColor: `${tokens?.accent?.primary}08`,
              },
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            {isProcessing ? (
              <CircularProgress size={40} sx={{ color: tokens?.accent?.primary }} />
            ) : file ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <FileIcon sx={{ fontSize: 40, color: tokens?.accent?.primary }} />
                <Typography sx={{ color: tokens?.text?.primary, fontWeight: 500 }}>
                  {file.name}
                </Typography>
                <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '12px' }}>
                  Clique para selecionar outro arquivo
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <UploadIcon sx={{ fontSize: 40, color: tokens?.text?.tertiary }} />
                <Typography sx={{ color: tokens?.text?.primary, fontWeight: 500 }}>
                  Arraste o arquivo CSV aqui
                </Typography>
                <Typography sx={{ color: tokens?.text?.tertiary, fontSize: '13px' }}>
                  ou clique para selecionar
                </Typography>
              </Box>
            )}
          </Box>

          {/* Botao de exemplo */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadExample}
              sx={{
                color: tokens?.text?.secondary,
                '&:hover': { color: tokens?.accent?.primary },
              }}
            >
              Baixar arquivo de exemplo
            </Button>
          </Box>
        </Box>

        {/* Resultado do parsing */}
        {parseResult && (
          <>
            <Divider sx={{ borderColor: tokens?.border?.subtle }} />

            <Box sx={{ p: 2 }}>
              {/* Status chips */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {hasDeliveries && (
                  <Chip
                    icon={<SuccessIcon sx={{ fontSize: '16px !important' }} />}
                    label={`${parseResult.deliveries.length} entregas encontradas`}
                    size="small"
                    sx={{
                      backgroundColor: `${tokens?.semantic?.success}20`,
                      color: tokens?.semantic?.success,
                      '& .MuiChip-icon': { color: 'inherit' },
                    }}
                  />
                )}
                {hasWarnings && (
                  <Chip
                    icon={<WarningIcon sx={{ fontSize: '16px !important' }} />}
                    label={`${parseResult.warnings.length} aviso(s)`}
                    size="small"
                    sx={{
                      backgroundColor: `${tokens?.semantic?.warning}20`,
                      color: tokens?.semantic?.warning,
                      '& .MuiChip-icon': { color: 'inherit' },
                    }}
                  />
                )}
                {hasErrors && (
                  <Chip
                    icon={<ErrorIcon sx={{ fontSize: '16px !important' }} />}
                    label={`${parseResult.errors.length} erro(s)`}
                    size="small"
                    sx={{
                      backgroundColor: `${tokens?.semantic?.error}20`,
                      color: tokens?.semantic?.error,
                      '& .MuiChip-icon': { color: 'inherit' },
                    }}
                  />
                )}
              </Box>

              {/* Alertas de erro/warning */}
              {hasErrors && (
                <Alert
                  severity="error"
                  sx={{ mb: 2 }}
                >
                  {parseResult.errors.slice(0, 5).map((err, idx) => (
                    <Box key={idx} component="div">
                      Linha {err.line}: {err.message}
                    </Box>
                  ))}
                  {parseResult.errors.length > 5 && (
                    <Box component="div" sx={{ mt: 1, fontWeight: 500 }}>
                      ... e mais {parseResult.errors.length - 5} erro(s)
                    </Box>
                  )}
                </Alert>
              )}

              {hasWarnings && (
                <Alert
                  severity="warning"
                  sx={{ mb: 2 }}
                >
                  {parseResult.warnings.map((warn, idx) => (
                    <Box key={idx} component="div">
                      {warn.message}
                    </Box>
                  ))}
                </Alert>
              )}

              {/* Preview da tabela */}
              {hasDeliveries && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px', mb: 1 }}>
                    Preview (primeiras {Math.min(MAX_PREVIEW_ROWS, parseResult.deliveries.length)} linhas):
                  </Typography>
                  <TableContainer
                    sx={{
                      maxHeight: 250,
                      backgroundColor: tokens?.surface?.base,
                      borderRadius: tokens?.radius?.md,
                      border: `1px solid ${tokens?.border?.subtle}`,
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ backgroundColor: tokens?.surface?.elevated, color: tokens?.text?.secondary }}>#</TableCell>
                          <TableCell sx={{ backgroundColor: tokens?.surface?.elevated, color: tokens?.text?.secondary }}>Nome</TableCell>
                          <TableCell sx={{ backgroundColor: tokens?.surface?.elevated, color: tokens?.text?.secondary }}>Endereco</TableCell>
                          <TableCell sx={{ backgroundColor: tokens?.surface?.elevated, color: tokens?.text?.secondary }}>Lat</TableCell>
                          <TableCell sx={{ backgroundColor: tokens?.surface?.elevated, color: tokens?.text?.secondary }}>Lng</TableCell>
                          <TableCell sx={{ backgroundColor: tokens?.surface?.elevated, color: tokens?.text?.secondary }}>Peso</TableCell>
                          <TableCell sx={{ backgroundColor: tokens?.surface?.elevated, color: tokens?.text?.secondary }}>Prioridade</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {parseResult.deliveries.slice(0, MAX_PREVIEW_ROWS).map((delivery, idx) => (
                          <TableRow key={delivery.id}>
                            <TableCell sx={{ color: tokens?.text?.tertiary }}>{idx + 1}</TableCell>
                            <TableCell sx={{ color: tokens?.text?.primary, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {delivery.name || '-'}
                            </TableCell>
                            <TableCell sx={{ color: tokens?.text?.secondary, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '12px' }}>
                              {delivery.address || '-'}
                            </TableCell>
                            <TableCell sx={{ color: tokens?.text?.secondary, fontSize: '12px' }}>{delivery.lat?.toFixed(4)}</TableCell>
                            <TableCell sx={{ color: tokens?.text?.secondary, fontSize: '12px' }}>{delivery.lng?.toFixed(4)}</TableCell>
                            <TableCell sx={{ color: tokens?.text?.secondary }}>{delivery.weight || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={delivery.priority}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '10px',
                                  backgroundColor:
                                    delivery.priority === 'high' ? `${tokens?.semantic?.error}20` :
                                    delivery.priority === 'low' ? `${tokens?.text?.tertiary}20` :
                                    `${tokens?.accent?.primary}20`,
                                  color:
                                    delivery.priority === 'high' ? tokens?.semantic?.error :
                                    delivery.priority === 'low' ? tokens?.text?.tertiary :
                                    tokens?.accent?.primary,
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Modo de importacao */}
              {hasDeliveries && existingDeliveriesCount > 0 && (
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: tokens?.surface?.elevated,
                    borderRadius: tokens?.radius?.md,
                    border: `1px solid ${tokens?.border?.subtle}`,
                  }}
                >
                  <Typography sx={{ color: tokens?.text?.secondary, fontSize: '13px', mb: 1 }}>
                    Voce ja tem {existingDeliveriesCount} entrega(s). Como deseja importar?
                  </Typography>
                  <RadioGroup
                    value={importMode}
                    onChange={(e) => setImportMode(e.target.value)}
                  >
                    <FormControlLabel
                      value="merge"
                      control={<Radio size="small" sx={{ color: tokens?.accent?.primary }} />}
                      label={
                        <Typography sx={{ color: tokens?.text?.primary, fontSize: '14px' }}>
                          Adicionar as entregas existentes (merge)
                        </Typography>
                      }
                    />
                    <FormControlLabel
                      value="replace"
                      control={<Radio size="small" sx={{ color: tokens?.accent?.primary }} />}
                      label={
                        <Typography sx={{ color: tokens?.text?.primary, fontSize: '14px' }}>
                          Substituir todas as entregas (replace)
                        </Typography>
                      }
                    />
                  </RadioGroup>
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${tokens?.border?.subtle}`,
          p: 2,
          gap: 1,
        }}
      >
        <Button onClick={handleClose} sx={{ color: tokens?.text?.secondary }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirmImport}
          disabled={!hasDeliveries}
          startIcon={<UploadIcon />}
          sx={{
            backgroundColor: tokens?.accent?.primary,
            '&:hover': {
              backgroundColor: tokens?.accent?.hover,
            },
            '&:disabled': {
              backgroundColor: tokens?.surface?.elevated,
            },
          }}
        >
          Importar {parseResult?.deliveries?.length || 0} Entrega(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CSVImportDialog;
